<template>
  <div class="w-full">
  <header
    class="flex items-center justify-between h-16 px-3 sm:px-5 bg-white text-neutral-500 border-b border-neutral-200 sticky top-0 w-full z-50"
  >
    <div class="flex items-center gap-0 sm:gap-6">
      <button
        class="lg:hidden p-2 hover:bg-neutral-500 hover:bg-opacity-10 rounded-md"
        @click="toggleMobileMenu"
      >
        <Icon icon="heroicons-solid:menu" height="24" />
      </button>
      <MobiusLogo size="sm" to="/dashboard" />
      <nav>
        <ul class="hidden lg:flex gap-0.5">
          <li v-for="navItem in desktopMenuComputed" :key="navItem.link">
            <router-link
              :to="navItem.link"
              class="flex items-center gap-1 h-9 px-4 py-2 hover:bg-neutral-500 hover:bg-opacity-10 rounded-md"
              active-class="text-neutral-700 bg-neutral-500 bg-opacity-10"
            >
              <Icon :icon="navItem.icon" :height="navItem.iconHeight" />
              {{ navItem.label }}
            </router-link>
          </li>
        </ul>
      </nav>
      <div
        v-if="showInstitutionDropdown"
        class="hidden lg:flex items-center relative"
        ref="institutionDropdownWrapper"
      >
        <button
          class="flex items-center gap-2 h-9 px-3 py-2 rounded-md border border-neutral-200 text-sm text-neutral-700 hover:bg-neutral-50"
          @click="toggleInstitutionDropdown"
        >
          <span class="font-medium">{{ selectedInstitutionName }}</span>
          <span v-if="selectedInstitutionCode" class="text-xs text-neutral-500">{{ selectedInstitutionCode }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M16.293 9.293L12 13.586L7.70697 9.293L6.29297 10.707L12 16.414L17.707 10.707L16.293 9.293Z" fill="#707070"/>
          </svg>
        </button>
        <div
          v-if="institutionDropdownOpen"
          class="absolute left-0 top-11 min-w-[240px] rounded-xl border border-neutral-200 bg-white shadow-lg z-50"
        >
          <button
            v-for="institution in activeInstitutions"
            :key="institution.id"
            class="w-full flex items-center justify-between px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
            @click="selectInstitution(institution.id)"
          >
            <div class="flex flex-col items-start">
              <span class="font-medium">{{ institution.name }}</span>
              <span v-if="institution.code" class="text-xs text-neutral-500">{{ institution.code }}</span>
            </div>
            <span v-if="String(institution.id) === String(selectedInstitutionInternal)" class="text-blue-600">✓</span>
          </button>
          <div class="border-t border-neutral-200">
            <button
              class="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
              @click="goToInstitutionManagement"
            >
              Administrar Instituciones
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-1 sm:gap-2 max-w-full overflow-x-hidden h-12">
      <div class="inline" v-if="!isPremiumUser && !isTeacherViewingStudentReport">
        <b-button
          class="flex items-center gap-1 sm:gap-2 justify-center text-[#ffba02] bg-amber-100 px-2 sm:px-3 py-1 rounded-full border-none text-xs sm:text-sm"
          @click="showModalPayment()"
        >
          <IconifyIcon :icon="icons.bxUpArrowCircle" height="24" />
          <span class="whitespace-nowrap hidden sm:block">Premium</span>
        </b-button>
      </div>
      <MobPaymentModal v-if="!isTeacherViewingStudentReport" ref="mobPaymentModal" />

      <router-link
        to="/admin-panel"
        v-if="role !== 'ROLE_STUDENT'"
        class="hidden md:flex items-center ml-2 mr-2"
      >
        <IconifyIcon :icon="icons.bxCog" height="24" />
        <span class="ml-1 whitespace-nowrap">Administración</span>
      </router-link>

      <button
        class="flex items-center justify-center gap-1 sm:gap-2 border border-blue-600 text-blue-600 bg-blue-200 bg-gradient-to-b from-blue-200 to-blue-50 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm max-w-full"
      >
        <Icon icon="ant-design:thunderbolt-outlined" height="18" class="flex-shrink-0 hidden sm:block" />
        <span class="whitespace-nowrap sm:hidden">{{ this.score }} pts</span>
        <span class="whitespace-nowrap hidden sm:inline">{{ this.score }} puntos</span>
      </button>
      <div class="flex items-center gap-1 relative" ref="notificationWrapper">
        <router-link
          to="/v2/settings"
          class="hidden lg:flex items-center gap-1 h-9 p-2 hover:bg-neutral-500 hover:bg-opacity-10 rounded-md"
        >
          <Icon icon="ph:nut" height="25" />
        </router-link>
        <button
          class="flex items-center gap-1 h-9 p-2 hover:bg-neutral-500 hover:bg-opacity-10 rounded-md relative"
          @click="toggleNotifications"
        >
          <Icon icon="codicon:bell" height="22" />
          <span
            v-if="pendingInvitationCount > 0"
            class="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center px-1"
          >
            {{ pendingInvitationCount }}
          </span>
        </button>

        <div
          v-if="showNotifications"
          class="fixed right-3 top-16 w-[360px] rounded-xl border border-neutral-200 bg-white shadow-lg z-[60]"
        >
          <div class="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
            <span class="text-sm font-semibold text-neutral-700">Invitaciones</span>
            <button class="text-neutral-400 hover:text-neutral-600" @click="showNotifications = false">
              ✕
            </button>
          </div>
          <div class="max-h-[360px] overflow-y-auto">
            <div v-if="isLoadingInvitations" class="px-4 py-6 text-sm text-neutral-500">
              Cargando invitaciones...
            </div>
            <div v-else-if="studentInvitations.length === 0" class="px-4 py-6 text-sm text-neutral-500">
              No tienes invitaciones pendientes.
            </div>
            <div
              v-else
              v-for="invitation in studentInvitations"
              :key="invitation.id"
              class="px-4 py-4 border-b border-neutral-100"
            >
              <div class="flex gap-3">
                <MobAvatar
                  :text="invitation.teacherName"
                  class="h-10 w-10"
                />
                <div class="flex-1">
                  <p class="text-sm font-semibold text-neutral-800">
                    {{ invitation.teacherName }} te ha invitado a unirte en Mobius.
                  </p>
                  <p class="text-xs text-neutral-500 mt-1">
                    Si aceptas, podrá ver tu progreso para ayudarte en tus estudios.
                  </p>
                  <div class="flex gap-2 mt-3">
                    <button
                      class="rounded-md border border-neutral-200 px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50"
                      @click="respondInvitation(invitation.id, 'REJECTED')"
                    >
                      Rechazar
                    </button>
                    <button
                      class="rounded-md bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700"
                      @click="respondInvitation(invitation.id, 'APPROVED')"
                    >
                      Aceptar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="hidden lg:flex items-center gap-3 pl-3 pr-2 py-1 hover:bg-neutral-500 hover:bg-opacity-10 rounded-md cursor-pointer relative"
        ref="userMenuWrapper"
        @click="toggleUserMenu"
      >
        <MobAvatar :text="userData.name" v-if="userData" />
        <div class="flex flex-col text-sm">
          <p class="text-neutral-700">{{ userData.name }}</p>
          <p v-if="userData.username" class="text-xs text-neutral-500">
            Tu ID: <span class="font-mono font-medium text-neutral-700">{{ userData.username }}</span>
          </p>
          <span>{{ this.roleText }}</span>
        </div>
        <div>
          <!-- prettier-ignore -->
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.293 9.293L12 13.586L7.70697 9.293L6.29297 10.707L12 16.414L17.707 10.707L16.293 9.293Z" fill="#707070"/>
          </svg>
        </div>
        <div
          v-if="userMenuOpen"
          class="fixed right-3 top-16 w-56 rounded-xl border border-neutral-200 bg-white shadow-lg z-[60]"
        >
          <div v-if="availableRoles.length > 1" class="py-1">
            <button
              v-for="item in availableRoles"
              :key="item.key"
              class="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 flex items-center justify-between"
              @click.stop="switchRole(item.key)"
            >
              <span>{{ item.label }}</span>
              <span v-if="item.key === roleInternal" class="text-blue-600">✓</span>
            </button>
          </div>
          <div class="border-t border-neutral-200">
            <button
              class="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
              @click.stop="logout"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      :class="[
        'bg-black bg-opacity-50 inset-0 fixed z-10',
        { hidden: !mobileMenuOpen },
      ]"
      @click="toggleMobileMenu"
    >
      <div class="w-[80%] bg-white min-h-full shadow-2xl">
        <div class="border-b h-16 px-5 items-center flex gap-6">
          <button
            class="p-2 hover:bg-neutral-500 hover:bg-opacity-10 rounded-md"
            @click.stop="toggleMobileMenu"
          >
            <Icon icon="heroicons:x-mark-20-solid" height="24" />
          </button>
          <MobiusLogo size="sm" to="/dashboard" />
        </div>
        <div class="p-4">
          <nav>
            <div
              class="flex items-center gap-3 pl-3 pr-2 py-1 hover:bg-neutral-500 hover:bg-opacity-10 rounded-md cursor-pointer"
            >
              <MobAvatar :text="userData.name" v-if="userData" />
              <div class="flex flex-col flex-grow">
                <p class="text-neutral-700">{{ userData.name }}</p>
                <p v-if="userData.username" class="text-xs text-neutral-500">
                  Tu ID: <span class="font-mono font-medium text-neutral-700">{{ userData.username }}</span>
                </p>
                <span class="text-sm">{{ this.roleText }}</span>
              </div>
            </div>
            <ul class="flex flex-col gap-1 mt-1">
              <li v-for="navItem in mobileMenuComputed" :key="navItem.link">
                <router-link
                  :to="navItem.link"
                  class="flex items-center gap-2 px-4 py-2 hover:bg-neutral-500 hover:bg-opacity-10 rounded-md"
                  active-class="text-neutral-700 bg-neutral-500 bg-opacity-10"
                >
                  <Icon :icon="navItem.icon" :height="navItem.iconHeight" />
                  {{ navItem.label }}
                </router-link>
              </li>
            </ul>
            <div v-if="availableRoles.length > 1" class="mt-4 pt-3 border-t border-neutral-200">
              <p class="px-4 text-xs text-neutral-500 mb-2">Cambiar rol</p>
              <button
                v-for="item in availableRoles"
                :key="item.key"
                class="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 flex items-center justify-between"
                @click="switchRole(item.key); mobileMenuOpen = false"
              >
                <span>{{ item.label }}</span>
                <span v-if="item.key === roleInternal" class="text-blue-600">✓</span>
              </button>
            </div>
            <div class="mt-4 pt-3 border-t border-neutral-200">
              <button
                class="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                @click="logout"
              >
                Cerrar sesión
              </button>
            </div>
          </nav>
        </div>
      </div>
    </div>
  </header>

  <!-- Sub-header móvil: selector de institución (sticky debajo del header) -->
  <div
    v-if="showInstitutionDropdown"
    class="lg:hidden sticky top-16 z-40 bg-white border-b border-neutral-200 px-3 py-2"
    ref="institutionDropdownWrapperMobile"
  >
    <button
      class="flex items-center gap-2 w-full min-w-0 text-left rounded-md py-1.5 px-2 -mx-2 hover:bg-neutral-50"
      @click="toggleInstitutionDropdown"
    >
      <Icon icon="heroicons:building-office-2" class="flex-shrink-0 text-neutral-500" height="20" />
      <span class="font-medium text-neutral-700 truncate flex-1">{{ selectedInstitutionName }}</span>
      <span v-if="selectedInstitutionCode" class="text-xs text-neutral-500 flex-shrink-0 hidden sm:inline">{{ selectedInstitutionCode }}</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" class="flex-shrink-0 text-neutral-500">
        <path d="M16.293 9.293L12 13.586L7.70697 9.293L6.29297 10.707L12 16.414L17.707 10.707L16.293 9.293Z" fill="currentColor"/>
      </svg>
    </button>
    <div
      v-if="institutionDropdownOpen"
      class="absolute left-3 right-3 mt-1 min-w-[200px] rounded-xl border border-neutral-200 bg-white shadow-lg z-50 max-h-[70vh] overflow-y-auto"
    >
      <button
        v-for="institution in activeInstitutions"
        :key="institution.id"
        class="w-full flex items-center justify-between px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
        @click="selectInstitution(institution.id)"
      >
        <div class="flex flex-col items-start min-w-0">
          <span class="font-medium truncate">{{ institution.name }}</span>
          <span v-if="institution.code" class="text-xs text-neutral-500">{{ institution.code }}</span>
        </div>
        <span v-if="String(institution.id) === String(selectedInstitutionInternal)" class="text-blue-600 flex-shrink-0 ml-2">✓</span>
      </button>
      <div class="border-t border-neutral-200">
        <button
          class="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
          @click="goToInstitutionManagement"
        >
          Administrar Instituciones
        </button>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue2";
import MobiusLogo from "@/components/MobiusLogo.vue";
import { MobAvatar } from "@/views/V2/components";
import { MobPaymentModal } from "@/views/V2/components";
import IconifyIcon from "@iconify/vue";
import bxLogIn from "@iconify/icons-bx/bx-log-in";
import bxMenu from "@iconify/icons-bx/bx-menu";
import bxCog from "@iconify/icons-bx/bx-cog";
import bxUpArrowCircle from "@iconify/icons-bx/bx-up-arrow-circle";
import { mapState } from "vuex";
import {
  getStudentInvitations,
  respondStudentInvitation,
} from "@/services/teacher-invitations.service";

export default {
  props: {
    role: String,
    userData: Object,
    menuItems: {
      type: Array,
      default: null,
    },
    mobileMenuItems: {
      type: Array,
      default: null,
    },
    institutions: {
      type: Array,
      default: () => [],
    },
    selectedInstitutionId: {
      type: [String, Number],
      default: null,
    },
  },
  name: "TopBar",
  components: {
    IconifyIcon,
    Icon,
    MobiusLogo,
    MobAvatar,
    MobPaymentModal,
  },
  data() {
    return {
      desktopMenu: [
        {
          label: "Cursos",
          icon: "codicon:mortar-board",
          iconHeight: "22",
          link: "/dashboard",
        },
        /*{
          label: "Estadísticas",
          icon: "ph:trend-up",
          iconHeight: "22",
          link: "/statistics",
        },*/
        /*{
          label: "Olimpiadas",
          icon: "solar:medal-star-outline",
          iconHeight: "22",
          link: "/olypics",
        },*/
      ],
      mobileMenu: [
        {
          label: "Cursos",
          icon: "codicon:mortar-board",
          iconHeight: "22",
          link: "/dashboard",
        },
        /*{
          label: "Estadísticas",
          icon: "ph:trend-up",
          iconHeight: "22",
          link: "/statistics",
        },*/
        /*{
          label: "Olimpiadas",
          icon: "solar:medal-star-outline",
          iconHeight: "22",
          link: "/v2/olypics",
        },*/
        // {
        //   label: "Configuración",
        //   icon: "ph:nut",
        //   iconHeight: "22",
        //   link: "/v2/config",
        // },
      ],
      icons: {
        bxLogIn,
        bxMenu,
        bxCog,
        bxUpArrowCircle
      },
      mobileMenuOpen: false,
      showNotifications: false,
      studentInvitations: [],
      isLoadingInvitations: false,
      roleText: null,
      roleInternal: null,
      score: "",
      selectedInstitutionInternal: null,
      institutionDropdownOpen: false,
      userMenuOpen: false,
    };
  },
  methods: {
    logout() {
      this.$store.dispatch("auth/logout");
      this.goToLogin();
    },
    goToLogin() {
      this.$router.push("/v2/auth/login").catch(() => {});
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen;
    },
    switchRole(roleKey) {
      if (!roleKey) {
        this.userMenuOpen = false;
        return;
      }
      localStorage.setItem("role", roleKey);
      this.roleInternal = roleKey;
      this.updateRoleText(roleKey);
      this.userMenuOpen = false;
      if (roleKey === "ROLE_ACADEMIC_ADMIN") {
        this.$router.push("/teacher/groups").catch(() => {});
      } else {
        this.$router.push("/dashboard").catch(() => {});
      }
    },
    updateRoleText(roleKey) {
      this.roleText =
        roleKey === "ROLE_STUDENT"
          ? "Estudiante"
          : roleKey === "ROLE_ACADEMIC_ADMIN"
          ? "Administrador académico"
          : "Administrador";
    },
    showModalPayment() {
      if (this.isTeacherViewingStudentReport) return;
      if (this.$refs["mobPaymentModal"]) this.$refs["mobPaymentModal"].showModalPayment();
    },
    hidePaymentModal() {
      const modal = this.$refs["mobPaymentModal"] && this.$refs["mobPaymentModal"].$refs && this.$refs["mobPaymentModal"].$refs["modal-payment-info"];
      if (modal && typeof modal.hide === "function") modal.hide();
    },
    emitInstitutionChange() {
      this.$emit("institution-change", this.selectedInstitutionInternal);
    },
    toggleInstitutionDropdown() {
      this.institutionDropdownOpen = !this.institutionDropdownOpen;
    },
    selectInstitution(institutionId) {
      this.selectedInstitutionInternal = institutionId;
      this.emitInstitutionChange();
      this.institutionDropdownOpen = false;
    },
    goToInstitutionManagement() {
      this.institutionDropdownOpen = false;
      this.$router.push("/teacher/institutions").catch(() => {});
    },
    async toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications) {
        await this.loadStudentInvitations();
      }
    },
    async loadStudentInvitations() {
      if (this.roleInternal !== "ROLE_STUDENT") {
        return;
      }
      this.isLoadingInvitations = true;
      try {
        this.studentInvitations = await getStudentInvitations();
      } catch (error) {
        console.error("Error cargando invitaciones", error);
      } finally {
        this.isLoadingInvitations = false;
      }
    },
    async respondInvitation(invitationId, status) {
      try {
        await respondStudentInvitation(invitationId, status);
        this.studentInvitations = this.studentInvitations.filter(
          (invitation) => invitation.id !== invitationId
        );
        this.$toasted.success(
          status === "APPROVED"
            ? "Invitación aceptada."
            : "Invitación rechazada.",
          { position: "top-center", duration: 3000 }
        );
      } catch (error) {
        console.error("Error respondiendo invitación", error);
        this.$toasted.error("No se pudo responder la invitación.", {
          position: "top-center",
          duration: 3000,
        });
      }
    },
    getInitials(name) {
      if (!name) {
        return "AA";
      }
      const parts = name.trim().split(/\s+/);
      const first = parts[0] ? parts[0][0] : "";
      const second = parts[1] ? parts[1][0] : "";
      const initials = `${first}${second}`.toUpperCase();
      return initials || "AA";
    },
    handleOutsideClick(event) {
      if (this.showNotifications) {
        const wrapper = this.$refs.notificationWrapper;
        if (wrapper && !wrapper.contains(event.target)) {
          this.showNotifications = false;
        }
      }
      if (this.institutionDropdownOpen) {
        const dropdownWrapper = this.$refs.institutionDropdownWrapper;
        const dropdownWrapperMobile = this.$refs.institutionDropdownWrapperMobile;
        const inDesktop = dropdownWrapper && dropdownWrapper.contains(event.target);
        const inMobile = dropdownWrapperMobile && dropdownWrapperMobile.contains(event.target);
        if (!inDesktop && !inMobile) {
          this.institutionDropdownOpen = false;
        }
      }
      if (this.userMenuOpen) {
        const userWrapper = this.$refs.userMenuWrapper;
        if (userWrapper && !userWrapper.contains(event.target)) {
          this.userMenuOpen = false;
        }
      }
    },
  },
  mounted: function () {
    this.roleInternal = this.role;
    this.updateRoleText(this.roleInternal);

    if (this.isTeacherViewingStudentReport) {
      this.$nextTick(() => this.hidePaymentModal());
    } else if (this.$route.params.openModalPayment) {
      this.showModalPayment();
    }
    if (this.roleInternal === "ROLE_STUDENT") {
      this.loadStudentInvitations();
    }
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  computed: {
    ...mapState(["auth"]),
    desktopMenuComputed() {
      return this.menuItems && this.menuItems.length ? this.menuItems : this.desktopMenu;
    },
    mobileMenuComputed() {
      return this.mobileMenuItems && this.mobileMenuItems.length
        ? this.mobileMenuItems
        : this.mobileMenu;
    },
    availableRoles() {
      const unique = Array.from(new Set(this.roleNames));
      const labels = {
        ROLE_STUDENT: "Estudiante",
        ROLE_ACADEMIC_ADMIN: "Administrador académico",
        ROLE_ADMIN: "Administrador",
      };
      return unique
        .filter((roleKey) => labels[roleKey])
        .map((roleKey) => ({ key: roleKey, label: labels[roleKey] }));
    },
    roleNames() {
      return (this.userData?.roles || []).map((item) => item.name);
    },
    showInstitutionDropdown() {
      return this.roleInternal === "ROLE_ACADEMIC_ADMIN" && this.activeInstitutions.length > 0;
    },
    activeInstitutions() {
      return (this.institutions || []).filter((institution) => institution.active !== false);
    },
    selectedInstitution() {
      const selectedId = this.selectedInstitutionInternal;
      return (
        this.activeInstitutions.find(
          (institution) => String(institution.id) === String(selectedId)
        ) || null
      );
    },
    selectedInstitutionName() {
      return this.selectedInstitution
        ? this.selectedInstitution.name
        : "Seleccionar institución";
    },
    selectedInstitutionCode() {
      return this.selectedInstitution && this.selectedInstitution.code
        ? this.selectedInstitution.code
        : "";
    },
    isTeacherViewingStudentReport() {
      const path = (this.$route.path || "").replace(/\/$/, "");
      return this.$route.name === "TeacherStudentReport" || /^\/teacher\/students\/\d+\/report$/.test(path);
    },
    isPremiumUser() {
      return this.$store.state.auth.isPremium;
    },
    pendingInvitationCount() {
      return this.studentInvitations.length;
    },
  },
  watch: {
    "$route.path": {
      handler(newPath) {
        if (this.isTeacherViewingStudentReport) this.hidePaymentModal();
      },
    },
    selectedInstitutionId: {
      immediate: true,
      handler(value) {
        this.selectedInstitutionInternal = value;
      },
    },
    role: {
      immediate: true,
      handler(newVal) {
        this.roleInternal = newVal;
        this.updateRoleText(newVal);
        if (newVal === "ROLE_STUDENT") {
          this.loadStudentInvitations();
        }
      },
    },
    "$store.state.levelStats.levelStats2": {
      handler(newValue) {
        this.score = newValue.puntuacion;
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    if (this.isTeacherViewingStudentReport) return;
    if (this.isPremiumUser === null) {
      this.$store.dispatch("auth/fetchPremiumStatus");
    }
  },
};
</script>
